// src/hooks/useSubmissionProviderOutputHydration.js
// -----------------------------------------------------------------------------
// Table-local provider-output hydration for Display Table / Session table.
//
// Enterprise rules:
//  • Do NOT call provider APIs from the frontend.
//  • Do NOT fetch Bitly clicks or metrics here.
//  • Do NOT add broad listeners to FormSubmissionsContext.
//  • Listen only to the visible/paged submission docs passed to this hook.
//  • Merge backend-enriched provider output into row copies for rendering only.
//
// Initial provider:
//  • bitly shortlink hydration from formSubmissions/{submissionId}
//
// Future-safe:
//  • Supports provider namespacing so HubSpot/Slack/etc. can reuse the pattern.
// -----------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  documentId,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

const FIRESTORE_IN_QUERY_LIMIT = 10;

function chunkArray(items = [], size = FIRESTORE_IN_QUERY_LIMIT) {
  const chunks = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
}

function normalizeProviderId(provider) {
  return String(provider || '').trim().toLowerCase();
}

function isProviderHydrationDebugEnabled() {
  return Boolean(
    typeof window !== 'undefined' &&
    window.__DEBUG_PROVIDER_HYDRATION__ === true
  );
}

function getRowSubmissionDocId(row = {}) {
  const id =
    row?.id ||
    row?.submissionDocId ||
    null;

  if (!id || typeof id !== 'string') {
    return null;
  }

  // Local optimistic rows do not exist in Firestore yet.
  // Once FormSubmissionsContext reconciles the row, id becomes the real doc id.
  if (id.startsWith('local_') || id.startsWith('local::')) {
    return null;
  }

  return id;
}

function buildProviderHydrationPatch({
  provider,
  data = {},
}) {
  const providerId = normalizeProviderId(provider);

  if (!providerId || !data || typeof data !== 'object') {
    return null;
  }

  // Bitly currently has compatibility fields in addition to canonical
  // shortlinks.bitly. Keep both because existing Display Card and backend code
  // already use them.
  if (providerId === 'bitly') {
    const bitly = data.shortlinks?.bitly || null;

    const shortUrl =
      data.bitlyShortUrl ||
      bitly?.shortUrl ||
      null;

    const status =
      data.bitlyShortlinkStatus ||
      bitly?.status ||
      null;

    const error =
      data.bitlyShortlinkError ||
      bitly?.error ||
      null;

    if (!shortUrl && !status && !error) {
      return null;
    }

    return {
      ...(shortUrl ? { bitlyShortUrl: shortUrl } : {}),
      ...(status ? { bitlyShortlinkStatus: status } : {}),
      ...(error ? { bitlyShortlinkError: error } : {}),
      ...(data.bitlyShortlinkUpdatedAt
        ? { bitlyShortlinkUpdatedAt: data.bitlyShortlinkUpdatedAt }
        : {}),
      shortlinks: {
        bitly: {
          provider: 'bitly',
          ...(bitly || {}),
          ...(shortUrl ? { shortUrl } : {}),
          ...(status ? { status } : {}),
          ...(error ? { error } : {}),
        },
      },
    };
  }

  // Generic provider-output namespace for future integrations.
  const integrationOutput =
    data.integrations?.[providerId] ||
    data.providerOutputs?.[providerId] ||
    null;

  if (!integrationOutput || typeof integrationOutput !== 'object') {
    return null;
  }

  return {
    integrations: {
      [providerId]: integrationOutput,
    },
  };
}

function removePendingProviderState(row = {}, provider = '') {
  const providerId = normalizeProviderId(provider);

  if (!providerId) {
    return row;
  }

  const next = { ...row };

  if (
    next._pendingShortlinks &&
    typeof next._pendingShortlinks === 'object'
  ) {
    const {
      [providerId]: _removedProvider,
      ...restPendingShortlinks
    } = next._pendingShortlinks;

    if (Object.keys(restPendingShortlinks).length > 0) {
      next._pendingShortlinks = restPendingShortlinks;
    } else {
      delete next._pendingShortlinks;
    }
  }

  if (
    next._pendingProviderOutputs &&
    typeof next._pendingProviderOutputs === 'object'
  ) {
    const {
      [providerId]: _removedProvider,
      ...restPendingProviderOutputs
    } = next._pendingProviderOutputs;

    if (Object.keys(restPendingProviderOutputs).length > 0) {
      next._pendingProviderOutputs = restPendingProviderOutputs;
    } else {
      delete next._pendingProviderOutputs;
    }
  }

  return next;
}

function mergeProviderPatchIntoRow({
  row,
  provider,
  patch,
}) {
  if (!row || !patch) {
    return row;
  }

  const providerId = normalizeProviderId(provider);
  let next = {
    ...row,
    ...patch,
  };

  if (patch.shortlinks && typeof patch.shortlinks === 'object') {
    next.shortlinks = {
      ...(row.shortlinks || {}),
      ...(patch.shortlinks || {}),
    };

    if (providerId && patch.shortlinks?.[providerId]) {
      next.shortlinks[providerId] = {
        ...(row.shortlinks?.[providerId] || {}),
        ...(patch.shortlinks[providerId] || {}),
      };
    }
  }

  if (patch.integrations && typeof patch.integrations === 'object') {
    next.integrations = {
      ...(row.integrations || {}),
      ...(patch.integrations || {}),
    };

    if (providerId && patch.integrations?.[providerId]) {
      next.integrations[providerId] = {
        ...(row.integrations?.[providerId] || {}),
        ...(patch.integrations[providerId] || {}),
      };
    }
  }

  next = removePendingProviderState(next, providerId);

  return next;
}

export default function useSubmissionProviderOutputHydration({
  enabled = false,
  provider = 'bitly',
  rows = [],
} = {}) {
  const providerId = normalizeProviderId(provider);
  const [hydratedBySubmissionId, setHydratedBySubmissionId] = useState({});

  const submissionDocIds = useMemo(() => {
    if (!enabled || !providerId) {
      return [];
    }

    const ids = (Array.isArray(rows) ? rows : [])
      .map(getRowSubmissionDocId)
      .filter(Boolean)
      .map(String);

    return Array.from(new Set(ids));
  }, [
    enabled,
    providerId,
    rows,
  ]);

  useEffect(() => {
    if (!enabled || !providerId || submissionDocIds.length === 0) {
      setHydratedBySubmissionId({});
      return undefined;
    }

    const activeSubmissionIds = new Set(submissionDocIds);

    // Prune hydration state to only the currently visible/paged rows.
    setHydratedBySubmissionId((prev) => {
      let changed = false;

      const next = Object.keys(prev || {}).reduce((acc, submissionId) => {
        if (activeSubmissionIds.has(submissionId)) {
          acc[submissionId] = prev[submissionId];
        } else {
          changed = true;
        }

        return acc;
      }, {});

      return changed ? next : prev;
    });

    const idChunks = chunkArray(submissionDocIds);

    const unsubscribers = idChunks.map((idChunk) => {
      const submissionsQuery = query(
        collection(db, 'formSubmissions'),
        where(documentId(), 'in', idChunk)
      );

      return onSnapshot(
        submissionsQuery,
        (snapshot) => {
          const nextPatches = {};

          snapshot.docs.forEach((submissionDoc) => {
            const submissionDocId = submissionDoc.id;
            const data = submissionDoc.data() || {};

            const patch = buildProviderHydrationPatch({
              provider: providerId,
              data,
            });

            if (!patch) {
              return;
            }

            nextPatches[submissionDocId] = patch;

            if (isProviderHydrationDebugEnabled()) {
              console.log('[useSubmissionProviderOutputHydration] provider snapshot:', {
                provider: providerId,
                submissionDocId,
                hasShortlinks: Boolean(data.shortlinks?.[providerId]),
                bitlyStatus:
                  providerId === 'bitly'
                    ? (data.bitlyShortlinkStatus || data.shortlinks?.bitly?.status || null)
                    : null,
                bitlyShortUrl:
                  providerId === 'bitly'
                    ? (data.bitlyShortUrl || data.shortlinks?.bitly?.shortUrl || null)
                    : null,
              });
            }
          });

          if (Object.keys(nextPatches).length === 0) {
            return;
          }

          setHydratedBySubmissionId((prev) => ({
            ...prev,
            ...nextPatches,
          }));
        },
        (error) => {
          console.warn('[useSubmissionProviderOutputHydration] hydration failed:', {
            provider: providerId,
            submissionDocIds: idChunk,
            error: error?.message || error,
          });
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [
    enabled,
    providerId,
    submissionDocIds,
  ]);

  const hydratedRows = useMemo(() => {
    if (!enabled || !providerId) {
      return rows;
    }

    return (Array.isArray(rows) ? rows : []).map((row) => {
      const submissionDocId = getRowSubmissionDocId(row);
      const patch = submissionDocId
        ? hydratedBySubmissionId[submissionDocId]
        : null;

      if (!patch) {
        return row;
      }

      return mergeProviderPatchIntoRow({
        row,
        provider: providerId,
        patch,
      });
    });
  }, [
    enabled,
    hydratedBySubmissionId,
    providerId,
    rows,
  ]);

  return {
    rows: hydratedRows,
    hydratedBySubmissionId,
    submissionDocIds,
  };
}